import { useState } from 'react'
import { ChevronDown } from 'lucide-react'
import { WatchProviders } from '../WatchProviders'
import { Collapse } from '../UI'

// ── AboutThisShow ─────────────────────────────────────────────────────────────
// The "read-once" reference content — binge time, vibe tags, where to watch —
// folded into one collapsed section (Phase 2 of the show-page rebuild, Jason
// 2026-07-07). The page now leads with the nightly act (Up Next → filmstrip →
// episodes); this stuff you look at once, so it gets out of the way behind a tap.
const MOODS = [
  { label: '🖤 Dark',       keys: ['dark','murder','violence','crime','death','psychological','disturbing','nihilism','brutal'] },
  { label: '😂 Funny',      keys: ['comedy','humor','satire','parody','absurdism','slapstick','sitcom','mockumentary'] },
  { label: '❤️ Heartfelt',  keys: ['friendship','family','love','feel good','coming of age','wholesome','found family'] },
  { label: '⚡ Intense',    keys: ['thriller','suspense','tension','twists','cliffhanger','adrenaline','action'] },
  { label: '🔮 Sci-Fi',     keys: ['science fiction','alien','space','future','robot','time travel','dystopia','artificial intelligence'] },
  { label: '🧙 Fantasy',    keys: ['magic','supernatural','dragon','wizard','mythology','fairy tale','fantasy world'] },
  { label: '🔍 Mystery',    keys: ['mystery','investigation','detective','conspiracy','whodunit','noir'] },
  { label: '💔 Emotional',  keys: ['drama','tragedy','grief','mental health','loss','redemption','tears'] },
  { label: '🏃 Action',     keys: ['action','adventure','fight','war','chase','heist','spy'] },
  { label: '📚 True Story', keys: ['based on true story','based on a true story','biographical','true crime'] },
]

export function AboutThisShow({ show }) {
  const [open, setOpen] = useState(false)

  const eps  = show?.number_of_episodes || 0
  const mins  = show?.episode_run_time?.[0] || 40
  const hasBinge = eps > 0
  const totalMin = mins * eps
  const hrs  = Math.floor(totalMin / 60)
  const days = (totalMin / 60 / 8).toFixed(1)

  const kws = (show?.keywords?.results || []).map(k => k.name.toLowerCase())
  const moods = MOODS.filter(m => m.keys.some(k => kws.some(kw => kw.includes(k)))).slice(0, 4)

  return (
    <div style={{ marginBottom: 14, border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden', background: 'var(--bg-card)' }}>
      <button
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'var(--font)',
          padding: '13px 15px', color: 'var(--text-strong)',
        }}
      >
        <span style={{ fontSize: 13, fontWeight: 500, fontFamily: 'var(--font-serif)', letterSpacing: '0.01em' }}>About this show</span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 11, color: 'var(--text-dim)', fontWeight: 600 }}>
            {[hasBinge ? `${hrs}h` : null, moods.length ? 'Vibes' : null, 'Where to watch'].filter(Boolean).join(' · ')}
          </span>
          <ChevronDown size={17} style={{ color: 'var(--text-dim)', transition: 'transform 0.2s ease', transform: open ? 'rotate(180deg)' : 'none' }} />
        </span>
      </button>

      <Collapse open={open}>
        <div style={{ padding: '2px 15px 15px' }}>
          {/* Binge time */}
          {hasBinge && (
            <div style={{ background: 'rgba(var(--amber-rgb),0.06)', border: '1px solid rgba(var(--amber-rgb),0.15)', borderRadius: 'var(--radius)', padding: '11px 14px', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 20 }}>⏱</span>
              <div>
                <div style={{ color: 'var(--amber)', fontWeight: 700, fontSize: 13 }}>
                  {hrs}h to binge {show.number_of_seasons > 1 ? `all ${show.number_of_seasons} seasons` : 'the series'}
                </div>
                <div style={{ color: 'var(--text-dim)', fontSize: 11, marginTop: 2 }}>
                  {eps} episodes × ~{mins} min · that's {days} days of your life
                </div>
              </div>
            </div>
          )}

          {/* Vibes */}
          {moods.length > 0 && (
            <div style={{ marginBottom: 14 }}>
              <div style={{ color: 'var(--text-dim)', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 8 }}>Vibes</div>
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                {moods.map(m => (
                  <span key={m.label} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: '5px 12px', color: 'var(--text)', fontSize: 13, fontWeight: 500 }}>
                    {m.label}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Where to watch */}
          <WatchProviders showId={show.id} />
        </div>
      </Collapse>
    </div>
  )
}
